<template>
  <div>
    <div class="row">
      <div class="col-md-2 d-flex">
        <custom-drop-down-rounded
          v-model="assigneeFilterSelection"
          :options="assigneeFilterOptions"
        />
      </div>
      <div class="col-md-3 d-flex">
        <custom-link
          class="justify-content-end font-14"
          text="Remover todas selección"
          icon="fa fa-minus"
          @click="checkUncheckHandler(false)"/>
        <custom-link
          class="justify-content-end ml-3 font-14"
          text="Seleccionar todos"
          icon="fa fa-plus"
          @click="checkUncheckHandler(true)"/>
      </div>
      <div class="col-md-7">
        <custom-input
          v-model="searchText"
          type="text"
          placeholder="Buscar permiso..."
          prepend-icon="fa fa-search"
          prepend-icon-text
          prepend-icon-type="success"
          :prepend-icon-left="false"
          @keyup="searchPermissionDebounced"
        />
      </div>
    </div>
    <div class="row">
      <div class="col mt-3">
        <div v-for="(item, index) in permissions" :key="`permission-item-${index}`">
          <custom-callout
            class="mb-1"
            :has-toggle="false"
            :border-color="getBorderColor(item.added, item.roleNames)"
          >
            <div class="d-flex justify-content-start align-items-center">
              <custom-check-box
                class="w-auto"
                v-model="item.added"
                :disabled="item.roleNames.length > 0"
                @input="handleToggleItem(item.id, $event)"
              />
              <span class="color-black font-16 my-1 pt-1">{{ item.name }}</span>
              <div
                v-for="(roleName, _index) in item.roleNames"
                :key="`role-name-${_index}`"
                class="d-flex align-items-center chips-container border-color--gray-5 border-radius mt-1 ml-3"
              >
                <em class="fas fa-shield-alt color-secondary-24 ml-2 font-14" />
                <span class="color-secondary-6 font-12 font-weight-normal pl-2 pr-3">
                  {{ roleName }}
                </span>
              </div>
            </div>
          </custom-callout>
        </div>
      </div>
    </div>
    <div
      class="row"
      v-if="pagination.totalItemCount >= defaultPageSize"
    >
      <div class="col-1 pt-4">
        <custom-drop-down
          v-model="perPage"
          :clearable="false"
          fieldkey="id"
          fieldname="name"
          :options="pagingList"
          @input="onPageSizeChanged"
        />
      </div>
      <div class="col-11">
        <pagination
          show-text
          :data="pagination"
          :page-size="perPage.id"
          @pagination-go-page="onPageChanged"
        />
      </div>
    </div>
  </div>
</template>

<script>
import debounce from 'lodash/debounce'
import Pagination from '@/components/common/Pagination'
import CustomDropDownRounded from '@/components/custom/CustomDropDownRounded.vue'
import { getPermissionsForUser } from '@/modules/users/api'
import { getPermissionsInRole } from '@/modules/roles/api'

import {
  CustomInput,
  CustomLink,
  CustomCallout,
  CustomCheckBox,
} from '@/components/common'
import CustomDropDown from 'custom-drop-down';

const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_ASSIGNEE_SELECTION = { id: 1, name: "Ver Todos" };
const ASSIGNEE_ASSIGNED_OPTION = { id: 2, name: "Asignados" };
const ASSIGNEE_NOTASSIGNED_OPTION = { id: 3, name: "No Asignados" };
const SPECIAL_PERMISSION_OPTION = { id: 4, name: "Permiso Especial" };

export default {
  name: 'PermissionSelection',
  components: {
    CustomInput,
    CustomLink,
    Pagination,
    CustomCallout,
    CustomCheckBox,
    CustomDropDown,
    CustomDropDownRounded
  },
  props: {
    userId: {
      type: String,
      required: false
    },
  },
  data: () => ({
    searchCriteria: null,
    permissions: [],
    permissionsToAdd: [],
    permissionsOriginalAssigned: [],
    permissionsToDelete: [],
    permissionsOriginalNotAssigned: [],
    searchText: '',
    assigneeFilterSelection: ASSIGNEE_ASSIGNED_OPTION,
    assigneeFilterOptions: [
      DEFAULT_ASSIGNEE_SELECTION,
      ASSIGNEE_ASSIGNED_OPTION,
      ASSIGNEE_NOTASSIGNED_OPTION,
      SPECIAL_PERMISSION_OPTION
    ],
    pagination: {
      totalItemCount: 0,
      count: DEFAULT_PAGE_SIZE,
      pageCount: 0,
      currentPage: 1,
    },
    defaultPageSize: DEFAULT_PAGE_SIZE,
    perPage: {
      id: DEFAULT_PAGE_SIZE,
      name: DEFAULT_PAGE_SIZE
    },
    pagingList: [
      { id: 20, name: 20, },
      { id: 50, name: 50, },
      { id: 100, name: 100, },
      { id: 500, name: 500, },
      { id: 100000, name: 'Todos', },
    ],
  }),
  computed: {
    roleId() {
      return this.$route.params.roleId;
    },
    getBorderColor() {
      return (added, roleNames) => {
        if(added && roleNames && roleNames.length > 0) return 'gray-5';
        return (added ? 'green' : 'gray-5')
      }
    },
  },
  watch: {
    assigneeFilterSelection: {
      deep: true,
      handler() {
        this.pagination.currentPage = 1;
        this.searchPermission();
      },
    },
  },
  methods: {
    async resetTempPermissions() {
      this.permissionsToAdd = [];
      this.permissionsToDelete = [];
      await this.searchPermission();
    },
    handleToggleItem(id, value) {
      if (value) {
        if (!this.permissionsOriginalAssigned.includes(id)) {
          this.permissionsToAdd.push(id);
        }

        this.permissionsToDelete = this.permissionsToDelete.filter(item => item !== id);
      }
      else {
        if (!this.permissionsOriginalNotAssigned.includes(id) && id) {
          this.permissionsToDelete.push(id);
        }
        this.permissionsToAdd = this.permissionsToAdd.filter(item => item !== id);
      }

      const removeApplied = this.permissionsOriginalAssigned.filter(x => !this.permissionsToDelete.includes(x));
      const selected = [...new Set([...this.permissionsToAdd, ...removeApplied])];

      this.$emit('on-select-permission', selected);
    },
    checkUncheckHandler(value) {
      const temp = this.permissions.map((x) => ({
        ...x,
        added: !x.roleNames?.length ? value : x.added
      }));

      this.permissions = [...temp];
    },
    async onPageChanged(currentPage) {
      this.pagination.currentPage = currentPage;
      this.searchPermission();
    },
    async onPageSizeChanged(perPage) {
      this.perPage = {...perPage};
      this.pagination.perPage = perPage.id;
      this.pagination.currentPage = 1;

      this.searchPermission();
    },
    searchPermissionDebounced: debounce(function(event) {
      this.searchText = event;
      this.pagination.currentPage = 1;
      this.searchPermission();
    }, 500),
    async searchUserPermission() {
      const request = {
        userId: this.userId,
        textFilter: this.searchText,
        asigneeFilter: this.assigneeFilterSelection?.id ?? this.assigneeFilterSelection,
        pageNumber: this.pagination.currentPage,
        pageSize: this.pagination.perPage,
        permissionsTempAdd: this.permissionsToAdd,
        permissionsTempDelete: this.permissionsToDelete,
      };

      const { data } = await getPermissionsForUser(request);
      return data;
    },
    async searchRolePermission() {
      const request = {
        roleId: this.roleId,
        textFilter: this.searchText,
        asigneeFilter: this.assigneeFilterSelection?.id ?? this.assigneeFilterSelection,
        pageNumber: this.pagination.currentPage,
        pageSize: this.pagination.perPage,
        permissionsTempAdd: this.permissionsToAdd,
        permissionsTempDelete: this.permissionsToDelete,
      };

      const { data } = await getPermissionsInRole(request);
      return data;
    },
    async searchPermission() {
      if (!this.assigneeFilterSelection?.id && !this.assigneeFilterSelection) return;

      let data;

      if (this.userId) {
        data = await this.searchUserPermission();
      }
      else {
        data = await this.searchRolePermission();
      }

      this.permissions = data.pagedResult.data.map(x => ({
        ...x,
        added: this.permissionsToAdd.includes(x.id) ? true : this.permissionsToDelete.includes(x.id) ? false : x.added
      }));
      this.pagination.count = data.pagedResult.data.length;
      this.pagination.pageCount = data.pagedResult.pageCount;
      this.pagination.totalItemCount = data.pagedResult.totalItemCount;

      this.permissionsOriginalAssigned = data.permissionsAssigned;
      this.permissionsOriginalNotAssigned = data.permissionsNotAssigned;
      this.handleToggleItem();
    },
  },
  async mounted() {
    await this.searchPermission();
  }
}
</script>

<style scoped lang="scss">
  .chips-container{
    border-radius: 24px;
  }
</style>
